import React, { type FC, useCallback, useState } from "react";
import { useRefreshEffect } from "../components/RefreshController";
import { useApiCall } from "./api";
import type {
  Assessor,
  Certificate,
  CertificateType,
  Company,
  DefaultInspectionAttachment,
  Exam,
  ExamParticipant,
  Project,
  RealmRole,
  User,
  WhitelistedEmail,
} from "./dto";
import type { Inspection } from "./dto/Inspection";
import type { QuestionSet } from "./dto/QuestionSet";
import type { MyCompanyPermissions } from "./dto/MyCompanyPermissions";

interface APIContextType {
  companies: Company[];
  projects: Project[];
  questionSets: QuestionSet[];
  assessors: Assessor[];
  users: User[];
  exams: Exam[];
  inspections: Inspection[];
  participants: ExamParticipant[];
  roles: RealmRole[];
  certificates: Certificate[];
  whitelistedEmails: WhitelistedEmail[];
  certificateTypes: CertificateType[];
  defaultInspectionAttachments: DefaultInspectionAttachment[];
  // The caller's own permissions, one row per company they can see, from `GET /v2/companies` (does NOT
  // special-case admins — see api.ts's getMyCompanyPermissions). Fetched once here, globally, rather
  // than per-page, since permission checks are relevant app-wide (nav visibility, page gates) the same
  // way `companies` is — see src/permissions/usePermissions.ts for the actual ability-check API built
  // on top of this list.
  myCompanyPermissions: MyCompanyPermissions[];
  loading: boolean;
  refreshAll: () => Promise<void>;
  refresh: {
    companies: (silent: boolean) => Promise<void>;
    projects: (silent: boolean) => Promise<void>;
    questionSets: (silent: boolean) => Promise<void>;
    exams: (silent: boolean) => Promise<void>;
    inspections: (silent: boolean) => Promise<void>;
    assessors: (silent: boolean) => Promise<void>;
    users: (silent: boolean) => Promise<void>;
    participants: (silent: boolean) => Promise<void>;
    roles: (silent: boolean) => Promise<void>;
    certificates: (silent: boolean) => Promise<void>;
    whitelistedEmails: (silent: boolean) => Promise<void>;
    certificateTypes: (silent: boolean) => Promise<void>;
    defaultInspectionAttachments: (silent: boolean) => Promise<void>;
    myCompanyPermissions: (silent: boolean) => Promise<void>;
  };
}
export const APIContext = React.createContext<APIContextType>({} as APIContextType);

export const APIContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    listCompanies,
    listProjects,
    listQuestionSets,
    listExams,
    listInspections,
    listAssessors,
    listAllParticipants,
    listUsers,
    listRoles,
    listCertificates,
    listWhitelistedEmails,
    listCertificateTypes,
    listDefaultInspectionAttachments,
    getMyCompanyPermissions,
  } = useApiCall();
  const companies = useFetchedResource(() => listCompanies());
  const projects = useFetchedResource(() => listProjects());
  const questionSets = useFetchedResource(() => listQuestionSets());
  const exams = useFetchedResource(() => listExams());
  const inspections = useFetchedResource(() => listInspections());
  const assessors = useFetchedResource(() => listAssessors());
  const participants = useFetchedResource(() => listAllParticipants());
  const participantsWithScore = useFetchedResource(() => listAllParticipants(true));
  const users = useFetchedResource(() => listUsers());
  const roles = useFetchedResource(() => listRoles());
  const certificates = useFetchedResource(() => listCertificates());
  const whitelistedEmails = useFetchedResource(() => listWhitelistedEmails());
  const certificateTypes = useFetchedResource(() => listCertificateTypes());
  const defaultInspectionAttachments = useFetchedResource(() => listDefaultInspectionAttachments());
  const myCompanyPermissions = useFetchedResource(() => getMyCompanyPermissions());

  const refreshAll = useCallback(async () => {
    await Promise.all([
      companies.reload(undefined),
      projects.reload(undefined),
      questionSets.reload(undefined),
      exams.reload(undefined),
      inspections.reload(undefined),
      assessors.reload(undefined),
      participants.reload(undefined),
      participantsWithScore.reload(undefined),
      users.reload(undefined),
      roles.reload(undefined),
      certificates.reload(undefined),
      whitelistedEmails.reload(undefined),
      certificateTypes.reload(undefined),
      defaultInspectionAttachments.reload(undefined),
      myCompanyPermissions.reload(undefined),
    ]);
  }, [
    companies.reload,
    projects.reload,
    questionSets.reload,
    exams.reload,
    inspections.reload,
    participants.reload,
    assessors.reload,
    users.reload,
    roles.reload,
    certificates.reload,
    whitelistedEmails.reload,
    certificateTypes.reload,
    defaultInspectionAttachments.reload,
    myCompanyPermissions.reload,
  ]);

  useRefreshEffect(() => {
    companies.reload(undefined);
    projects.reload(undefined);
    questionSets.reload(undefined);
    exams.reload(undefined);
    inspections.reload(undefined);
    assessors.reload(undefined);
    participants.reload(undefined);
    participantsWithScore.reload(undefined);
    users.reload(undefined);
    roles.reload(undefined);
    certificates.reload(undefined);
    whitelistedEmails.reload(undefined);
    certificateTypes.reload(undefined);
    defaultInspectionAttachments.reload(undefined);
    myCompanyPermissions.reload(undefined);
  });

  return (
    <APIContext.Provider
      value={{
        companies: companies.resource ?? [],
        projects: projects.resource ?? [],
        questionSets: questionSets.resource ?? [],
        assessors: assessors.resource ?? [],
        users: users.resource ?? [],
        participants: participantsWithScore.resource ?? participants.resource ?? [],
        exams: exams.resource ?? [],
        inspections: inspections.resource ?? [],
        roles: roles.resource ?? [],
        certificates: certificates.resource ?? [],
        whitelistedEmails: whitelistedEmails.resource ?? [],
        certificateTypes: certificateTypes.resource ?? [],
        defaultInspectionAttachments: defaultInspectionAttachments.resource ?? [],
        myCompanyPermissions: myCompanyPermissions.resource ?? [],
        loading:
          companies.loading ||
          projects.loading ||
          questionSets.loading ||
          assessors.loading ||
          exams.loading ||
          inspections.loading ||
          participants.loading ||
          roles.loading ||
          certificates.loading ||
          certificateTypes.loading ||
          defaultInspectionAttachments.loading ||
          myCompanyPermissions.loading,
        refreshAll,
        refresh: {
          companies: async (silent: boolean) => {
            await companies.reload(undefined, silent);
          },
          projects: async (silent: boolean) => {
            await projects.reload(undefined, silent);
          },
          questionSets: async (silent: boolean) => {
            await questionSets.reload(undefined, silent);
          },
          exams: async (silent: boolean) => {
            await exams.reload(undefined, silent);
          },
          inspections: async (silent: boolean) => {
            await inspections.reload(undefined, silent);
          },
          assessors: async (silent: boolean) => {
            await assessors.reload(undefined, silent);
          },
          users: async (silent: boolean) => {
            await users.reload(undefined, silent);
          },
          participants: async (silent: boolean) => {
            await Promise.all([
              participants.reload(undefined, silent),
              participantsWithScore.reload(undefined, silent),
            ]);
          },
          roles: async (silent: boolean) => {
            await roles.reload(undefined, silent);
          },
          certificates: async (silent: boolean) => {
            await certificates.reload(undefined, silent);
          },
          whitelistedEmails: async (silent: boolean) => {
            await whitelistedEmails.reload(undefined, silent);
          },
          certificateTypes: async (silent: boolean) => {
            await certificateTypes.reload(undefined, silent);
          },
          defaultInspectionAttachments: async (silent: boolean) => {
            await defaultInspectionAttachments.reload(undefined, silent);
          },
          myCompanyPermissions: async (silent: boolean) => {
            await myCompanyPermissions.reload(undefined, silent);
          },
        },
      }}
    >
      {children}
    </APIContext.Provider>
  );
};

export function useApi() {
  return React.useContext(APIContext);
}

export function useFetchedResource<T, P = undefined>(get: (param: P) => Promise<T>) {
  const [resource, setResource] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchResource = useCallback(async (param: P, fetchSilently = false) => {
    if (!fetchSilently) {
      setLoading(true);
    }
    let fetchedResource = null;
    try {
      fetchedResource = await get(param);
      setResource(fetchedResource);
    } catch (error) {
      // @ts-expect-error
      setError(error);
    } finally {
      if (!fetchSilently) {
        setLoading(false);
      }
    }
    return fetchedResource;
  }, []);

  return { resource, error, loading, reload: fetchResource };
}
